import {
    Box, CircularProgress, Dialog, DialogContent, IconButton,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Tooltip, Typography,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useSelector } from 'react-redux';
import { AppState } from '../../store/rootReducer';

interface Props {
    open: boolean;
    loading: boolean;
    orderDetail: any;
    onClose: () => void;
}

const BG         = '#1e1e1e';   // neutral charcoal
const BG_TABLE   = '#242424';
const BG_ROW_ODD = '#2a2a2a';
const LBL        = '#9aa0a6';   // muted neutral-gray labels
const VAL        = '#fdd835';   // yellow values
const RED        = '#d30535';   // Vitec red
const WHT        = '#eaeaea';   // near-white for structural text
const DIV        = '#3a3a3a';   // dividers
const HOVER      = '#333333';   // row hover — lighter than the row backgrounds for visible feedback
const labelSx = {
    fontSize: '0.80rem',
    color: LBL,
    whiteSpace: 'nowrap' as const,
    lineHeight: 1,
};

const valueSx = {
    fontSize: '0.95rem',
    color: VAL,
    whiteSpace: 'nowrap' as const,
    lineHeight: 1,
    fontWeight: 400,
};

const cellSx = {
    fontSize: '0.88rem',
    color: VAL,
    borderBottom: `1px solid ${DIV}`,
    py: '6px',
    px: '10px',
    whiteSpace: 'nowrap' as const,
    backgroundColor: 'transparent',
};

function F({ lbl, val }: { lbl: string; val?: any }) {
    return (
        <Box sx={{ display: 'inline-flex', alignItems: 'baseline', gap: '5px', flexShrink: 0 }}>
            <Typography sx={labelSx}>{lbl}</Typography>
            <Typography sx={valueSx}>{val ?? '—'}</Typography>
        </Box>
    );
}

function HRow({ children }: { children: React.ReactNode }) {
    return (
        <Box sx={{
            display: 'flex', flexWrap: 'wrap', gap: '24px',
            px: 2.5, py: '9px', alignItems: 'baseline',
            borderBottom: `1px solid ${DIV}`,
        }}>
            {children}
        </Box>
    );
}

const BASE_TABLE_COLS = [
    { label: 'ANTALL',         align: 'right'  as const, tip: '' },
    { label: 'ALF',            align: 'left'   as const, tip: '' },
    { label: 'ARTIKKELNR.',    align: 'left'   as const, tip: '' },
    { label: 'VAREBETEGNELSE', align: 'left'   as const, tip: '' },
    { label: 'V.PRIS',         align: 'right'  as const, tip: 'Pris pr. stk' },
    { label: 'RABATT%',        align: 'right'  as const, tip: '' },
    { label: 'NTO.F.PRIS',     align: 'right'  as const, tip: 'Nettopris × antall' },
];

const MARGIN_TABLE_COLS = [
    { label: 'KOSTPRIS', align: 'right' as const, tip: 'Innkjøpspris × antall' },
    { label: 'GRAD',     align: 'right' as const, tip: 'Dekningsgrad for linjen' },
];

const REST_TABLE_COLS = [
    { label: 'ANT. LEV.',      align: 'right'  as const, tip: '' },
    { label: 'PT',             align: 'center' as const, tip: '' },
    { label: 'PT-TYPE',        align: 'left'   as const, tip: '' },
];

export default function OrderDetailDialog({ open, loading, orderDetail, onClose }: Props) {
    // The logged-in company's name, as in the COBOL screen's line 1 (CompanyName in the token is space-padded).
    const companyName = useSelector((state: AppState) => state.auth.userJWT?.CompanyName)?.trim() ?? '';

    const deliveryLines = [
        orderDetail?.vnavn1, orderDetail?.vnavn2, orderDetail?.vnavn3, orderDetail?.vnavn4,
    ].filter(Boolean);

    const formatTotal = (v: number | null | undefined) => {
        if (v == null) return '—';
        return new Intl.NumberFormat('nb-NO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v);
    };

    const formatPct = (v: number | null | undefined) => {
        if (v == null) return '—';
        return `${new Intl.NumberFormat('nb-NO', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(v)} %`;
    };

    // BTF/Grad/Kostpris are only present when the caller is authorised to see contribution margin.
    const hasMargin = orderDetail?.dekn_grad != null;
    const tableCols = [...BASE_TABLE_COLS, ...(hasMargin ? MARGIN_TABLE_COLS : []), ...REST_TABLE_COLS];

    // SUM M/MVA — what the customer actually paid (incl. VAT). total_pris is excl. VAT; assumes
    // standard 25% Norwegian rate, matching the legacy screen's own computed value.
    const sumMedMva = orderDetail?.total_pris != null ? orderDetail.total_pris * 1.25 : null;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 1,
                    overflow: 'hidden',
                    backgroundColor: BG,
                    color: WHT,
                    border: `1px solid ${DIV}`,
                    minWidth: 900,
                },
            }}
            maxWidth="lg"
        >
            {/* Title bar — matches COBOL line 1 */}
            <Box sx={{
                backgroundColor: BG,
                borderBottom: `1px solid ${DIV}`,
                px: 2, py: '8px',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            }}>
                <Box sx={{ display: 'flex', gap: '28px', alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <Typography sx={{ ...labelSx, fontSize: '0.88rem' }}>{companyName}</Typography>
                    <Typography sx={{ fontSize: '1.05rem', color: RED, fontWeight: 700, letterSpacing: '0.08em' }}>
                        ORDRE SPØRRING.
                    </Typography>
                    <Box sx={{ display: 'inline-flex', alignItems: 'baseline', gap: '6px' }}>
                        <Typography sx={{ ...labelSx, fontSize: '0.88rem' }}>ORDRE</Typography>
                        <Box sx={{
                            backgroundColor: '#e8e8e8', color: '#111',
                            fontSize: '1.0rem', fontWeight: 700,
                            px: '10px', py: '2px', letterSpacing: '0.04em',
                        }}>
                            {orderDetail?.orderNumber ?? '—'}
                        </Box>
                    </Box>
                </Box>
                <IconButton onClick={onClose} size="small" sx={{ color: LBL, '&:hover': { color: WHT } }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            </Box>

            <DialogContent sx={{ p: 0, backgroundColor: BG }}>
                {loading ? (
                    <Box display="flex" justifyContent="center" p={5}>
                        <CircularProgress sx={{ color: RED }} />
                    </Box>
                ) : orderDetail ? (
                    <Box>
                        {/* Row: KNR  NVN  KRED.TYPE */}
                        <HRow>
                            <F lbl="KNR" val={orderDetail.kunde} />
                            <F lbl="NVN" val={orderDetail.navn1} />
                            <Box sx={{ ml: 'auto' }}>
                                <F lbl="KRED.TYPE=" val={orderDetail.kredtype} />
                            </Box>
                        </HRow>

                        {/* Row: RKN  ADR  PNR  STED */}
                        <HRow>
                            <Typography sx={labelSx}>RKN</Typography>
                            <F lbl="ADR"  val={orderDetail.adr} />
                            <F lbl="PNR"  val={orderDetail.postnr} />
                            <F lbl="STED" val={orderDetail.poststed} />
                        </HRow>

                        {/* Row: V.ADR (delivery address lines, conditional) */}
                        {deliveryLines.map((line: string, i: number) => (
                            <HRow key={i}>
                                <Typography sx={{ ...labelSx, minWidth: 40 }}>{i === 0 ? 'V.ADR' : ''}</Typography>
                                <Typography sx={valueSx}>{line}</Typography>
                            </HRow>
                        ))}

                        {/* Row: O.MOT  AVD  BET.KODE  LK  BK  ODATO */}
                        <HRow>
                            <F lbl="O.MOT"    val={orderDetail.orderRecipient} />
                            <F lbl="AVD"      val={orderDetail.avd} />
                            <F lbl="BET.KODE" val={orderDetail.BETMET} />
                            <F lbl="LK"       val={orderDetail.lager} />
                            <F lbl="BK"       val={orderDetail.processingCode} />
                            <F lbl="SELGER"   val={orderDetail.orderTradingDistrict} />
                            <Box sx={{ ml: 'auto' }}>
                                <F lbl="ODATO" val={orderDetail.dato} />
                            </Box>
                        </HRow>

                        {/* Row: SKAF  REST  GEB  ANFØR FRAKT  MVA */}
                        <HRow>
                            <F lbl="SKAF"        val={orderDetail.fullyDispatchedOrder} />
                            <F lbl="REST"        val={orderDetail.restkode} />
                            <F lbl="GEB"         val={orderDetail.gebyr} />
                            <F lbl="ANFØR FRAKT" val={orderDetail.anfor_frakt} />
                            <F lbl="MVA"         val={orderDetail.fritt === '1' ? 'Avgiftsfritt' : orderDetail.fritt} />
                        </HRow>

                        {/* Row: REKV.NR  FORS.M  SUM M/MVA — what the customer actually paid, incl. VAT */}
                        <HRow>
                            <F lbl="REKV.NR" val={orderDetail.rekvnr} />
                            <F lbl="FORS.M"  val={orderDetail.sendeMote} />
                            <Box sx={{ ml: 'auto', display: 'inline-flex', alignItems: 'baseline', gap: '6px' }}>
                                <Typography sx={{ fontSize: '0.82rem', color: RED, fontWeight: 700 }}>
                                    SUM M/MVA
                                </Typography>
                                <Typography sx={{ fontSize: '1.1rem', color: RED, fontWeight: 700 }}>
                                    {formatTotal(sumMedMva)}
                                </Typography>
                            </Box>
                        </HRow>

                        {/* Row: SUM U/MVA (always shown) + SUM SELVK / BTF / DEKN.GRAD (margin — gated) */}
                        <HRow>
                            <Box sx={{ ml: 'auto', display: 'inline-flex', alignItems: 'baseline', gap: '24px' }}>
                                <F lbl="SUM U/MVA" val={formatTotal(orderDetail.total_pris)} />
                                {hasMargin && (
                                    <>
                                        <F lbl="SUM SELVK" val={formatTotal(orderDetail.sum_selvk)} />
                                        <F lbl="BTF" val={formatTotal(orderDetail.btf)} />
                                        <Box sx={{ display: 'inline-flex', alignItems: 'baseline', gap: '6px' }}>
                                            <Typography sx={{ fontSize: '0.82rem', color: RED, fontWeight: 700 }}>
                                                DEKN.GRAD
                                            </Typography>
                                            <Typography sx={{ fontSize: '1.1rem', color: RED, fontWeight: 700 }}>
                                                {formatPct(orderDetail.dekn_grad)}
                                            </Typography>
                                        </Box>
                                    </>
                                )}
                            </Box>
                        </HRow>

                        {/* Line items table */}
                        {orderDetail.varer?.length > 0 ? (
                            <TableContainer sx={{ maxHeight: '55vh', backgroundColor: BG_TABLE }}>
                                <Table stickyHeader size="small" sx={{ tableLayout: 'auto' }}>
                                    <TableHead>
                                        <TableRow>
                                            {tableCols.map(({ label, align, tip }) => (
                                                <TableCell key={label} align={align} sx={{
                                                    backgroundColor: BG,
                                                    color: WHT,
                                                    fontWeight: 700,
                                                    fontSize: '0.78rem',
                                                    letterSpacing: '0.06em',
                                                    borderBottom: `2px solid ${RED}`,
                                                    py: '7px', px: '10px',
                                                    whiteSpace: 'nowrap',
                                                }}>
                                                    {tip ? (
                                                        <Tooltip title={tip} arrow>
                                                            <span style={{ borderBottom: `1px dotted ${LBL}`, cursor: 'help' }}>{label}</span>
                                                        </Tooltip>
                                                    ) : label}
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {orderDetail.varer.map((item: any, idx: number) => (
                                            <TableRow key={idx} sx={{
                                                backgroundColor: idx % 2 === 0 ? BG_TABLE : BG_ROW_ODD,
                                                '&:hover': { backgroundColor: HOVER },
                                            }}>
                                                <TableCell align="right" sx={cellSx}>{item.bestilt}</TableCell>
                                                <TableCell sx={cellSx}>{item.alfa}</TableCell>
                                                <TableCell sx={cellSx}>{item.artikkel}</TableCell>
                                                <TableCell sx={{ ...cellSx, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', color: WHT }}>{item.tekst}</TableCell>
                                                <TableCell align="right" sx={cellSx}>{item.vpris}</TableCell>
                                                <TableCell align="right" sx={cellSx}>
                                                    {[item.rab1, item.rab2, item.rab3].filter((r: any) => r != null && r !== 0 && r !== '').join(' | ') || '—'}
                                                </TableCell>
                                                <TableCell align="right" sx={cellSx}>{item.npris}</TableCell>
                                                {hasMargin && (
                                                    <>
                                                        <TableCell align="right" sx={cellSx}>{formatTotal(item.kostpris)}</TableCell>
                                                        <TableCell align="right" sx={cellSx}>{formatPct(item.grad)}</TableCell>
                                                    </>
                                                )}
                                                <TableCell align="right" sx={cellSx}>{item.levert}</TableCell>
                                                <TableCell align="center" sx={cellSx}>{item.tillegg}</TableCell>
                                                <TableCell sx={cellSx}>{item.pris_type || '—'}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        ) : (
                            <Box sx={{ px: 2, py: 3 }}>
                                <Typography sx={{ ...labelSx, color: LBL }}>Ingen ordrelinjer funnet.</Typography>
                            </Box>
                        )}
                    </Box>
                ) : (
                    <Box sx={{ px: 2, py: 4 }}>
                        <Typography sx={labelSx}>Ingen data tilgjengelig.</Typography>
                    </Box>
                )}
            </DialogContent>
        </Dialog>
    );
}
